import React, { useState,useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Stack,
  Box,
  IconButton,
  Button,
  TextField,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { getDeviceStationId } from "../utils/global_functions";

import { apiFetch } from "../utils/apiFetch";

// ── helper: normalize the saved API base URL ────────────────────────────────
const normalizeBaseUrl = (raw) => {
  const s = String(raw ?? "").trim().replace(/\/$/, "");
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s}`;
};


// ── component ───────────────────────────────────────────────────────────────
export default function EditTotalTransportationDialog({ open, onClose, onSaved, selectedDate, empId,totals=[]}) {
  
  // ── find the day right after the latest saved dateTo, within the selected month ──
  const getDefaultDateFrom = () => {
    const monthStart = selectedDate ? selectedDate.startOf("month") : dayjs().startOf("month");
    const monthEnd   = selectedDate ? selectedDate.endOf("month")   : dayjs().endOf("month");

    if (!totals.length) return monthStart;

    // find the latest dateTo among all existing records
    const latestDateTo = totals.reduce((latest, t) => {
      const to = dayjs(t.dateTo);
      return to.isAfter(latest) ? to : latest;
    }, dayjs(0));

    const nextStart = latestDateTo.add(1, "day");

    // if the next available day falls outside this month, fall back to month start
    if (nextStart.isAfter(monthEnd)) return monthStart;
    return nextStart;
  };
  
  // ── default shape for one empty row ────────────────────────────────────────
  const emptyRow = () => ({
    // To start month from we ended the last save click not start month from begening!
    dateFrom: getDefaultDateFrom(),
    dateTo:   selectedDate ? selectedDate.endOf("month") : dayjs().endOf("month"),
    amount:   "",
    km:       "",
    parking:  "",
  });
  const [rows, setRows]     = useState([emptyRow()]);
  const [saving, setSaving] = useState(false);
//overlap Error!!
  const [rowErrors, setRowErrors] = useState({});

  // ── row helpers ────────────────────────────────────────────────────────
  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const removeRow = (idx) =>
    setRows((prev) => prev.filter((_, i) => i !== idx));

  const updateRow = (idx, field, value) =>
    setRows((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row))
    );

//to reset when dialog opens or month changes
useEffect(() => {
  if (open) {
    setRows([emptyRow()]);
    setRowErrors({});
  }
}, [open, selectedDate]);    

  // ── save all rows ──────────────────────────────────────────────────────
  const handleSave = async () => {
  setSaving(true);
  setRowErrors({});
  setSaving(true);
  setRowErrors({});

  // ── validate: all dates must be within the selected month ──────────
  const newErrors = {};
  let hasError = false;

  const allowedMonth = selectedDate ? selectedDate.month() : dayjs().month();
  const allowedYear  = selectedDate ? selectedDate.year()  : dayjs().year();

  rows.forEach((row, idx) => {
    const from = row.dateFrom ? dayjs(row.dateFrom) : null;
    const to   = row.dateTo   ? dayjs(row.dateTo)   : null;

    const fromWrong = from && (from.month() !== allowedMonth || from.year() !== allowedYear);
    const toWrong   = to   && (to.month()   !== allowedMonth || to.year()   !== allowedYear);

    if (fromWrong || toWrong) {
      const monthLabel = (selectedDate || dayjs()).format("MMMM YYYY");
      newErrors[idx] = `Dates must be within ${monthLabel} only. Please correct the date range.`;
      hasError = true;
      return;  // date error already set for this row — don't overwrite it below
    }

    // Amount or Km is required — Parking alone isn't enough. Since a row
    // can never be saved empty anymore, Save now always sends it straight
    // to Pending (the backend upsert sets status=1 on insert).
    const hasAmount = Number(row.amount) > 0;
    const hasKm = Number(row.km) > 0;
    if (!hasAmount && !hasKm ) {
      newErrors[idx] = "Please fill in Amount or Km (Parking is optional).";
      hasError=true;

    }
  });

  if (hasError) {
    setRowErrors(newErrors);
    setSaving(false);
    return;
  }
  // ── proceed with save only if all rows are valid ──────────────────
  try {
    const rawBase  = localStorage.getItem("saved_sett_api_url");
    const API_BASE = normalizeBaseUrl(rawBase) || "http://localhost:5100";
    // const empId    = localStorage.getItem("shared_empid");
    const stationId = getDeviceStationId();

    const newErrors = {};
    let hasError = false;

    // send rows one by one so we can handle per-row errors
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const res = await apiFetch(`${API_BASE}/api/visit/total`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // empId: selectedEmpId,
          empId   :empId,
          dateFrom: row.dateFrom ? row.dateFrom.format("YYYY-MM-DD") : "",
          dateTo:   row.dateTo   ? row.dateTo.format("YYYY-MM-DD")   : "",
          amount:   row.amount,
          km:       row.km,
          parking:  row.parking,
          stationId,
          isPhone: true,
        }),
      });

      if (res.status === 409) {
        const data = await res.json();
        newErrors[i] = data.message || "Date range overlaps with an existing record.";
        hasError = true;
      } else if (!res.ok) {
        //ctaches 400/500/etc- anything tht is not success and is not the overlap case
        const data = await res.json().catch(() => ({}));
        newErrors[i]= data.error || data.message || "Save failed for this row. ";
        hasError = true;
      }
    }

    if (hasError) {
      setRowErrors(newErrors);
      return; // don't close dialog — show errors instead
    }

    // all rows saved successfully
    setRows([emptyRow()]);
    setRowErrors({});
    onClose();
    if (onSaved) onSaved();

  } catch (err) {
    console.error("EditTotalTransportation: save failed:", err);
  } finally {
    setSaving(false);
  }
};

  // ── render ─────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      {/* title */}
      <DialogTitle sx={{ fontWeight: 900, color: "#004d40", pb: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <DirectionsCarIcon sx={{ color: "#00796b" }} />
          <span>Total Transportation</span>
        </Stack>
      </DialogTitle>

      {/* content */}
      <DialogContent sx={{ bgcolor: "#f5f9f8", pt: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={1.5}>

            {rows.map((row, idx) => (
              <Paper
                key={idx}
                elevation={2}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  borderLeft: "4px solid #00796b",
                  bgcolor: "white",
                }}
              >
                {/* date range */}
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  sx={{ mb: 1.5 }}
                >
                  <DatePicker
                    label="Date From"
                    value={row.dateFrom}
                    onChange={(v) => updateRow(idx, "dateFrom", v)}
                    minDate={dayjs().startOf("month")}
                    maxDate={dayjs().endOf("month")}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        sx: { bgcolor: "white", borderRadius: 2 },
                      },
                    }}
                  />
                  <DatePicker
                    label="Date To"
                    value={row.dateTo}
                    onChange={(v) => updateRow(idx, "dateTo", v)}
                    minDate={dayjs().startOf("month")}
                    maxDate={dayjs().endOf("month")}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        sx: { bgcolor: "white", borderRadius: 2 },
                      },
                    }}
                  />
                </Stack>

                {/* amount + km */}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 1.5 }}>
                  <TextField
                    label="Amount"
                    type="number"
                    size="small"
                    fullWidth
                    value={row.amount}
                    onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^\d]/g, "");
                    updateRow(idx, "amount", cleaned);
                    }}
                    inputProps={{ min: 0, step: 50 }}
                    disabled={saving}
                    sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    InputProps={{
                      endAdornment: (
                        <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                          L.L
                        </Typography>
                      ),
                    }}
                  />
                  <TextField
                    label="Kilometrage"
                    type="number"
                    size="small"
                    fullWidth
                    value={row.km}
                    onChange={(e) => updateRow(idx, "km", e.target.value)}
                    inputProps={{ min: 0, step: 0.1 }}
                    disabled={saving}
                    sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 }, "& .MuiInputLabel-root": { fontSize: 13 } }}
                    InputProps={{
                      endAdornment: (
                        <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                          Km
                        </Typography>
                      ),
                    }}
                  />
                </Stack>

                {/* parking + delete */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <TextField
                    label="Parking"
                    type="number"
                    size="small"
                    fullWidth
                    value={row.parking}
                    onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^\d]/g, "");
                    updateRow(idx, "parking", cleaned);
                    }}
                    inputProps={{ min: 0, step: 50 }}
                    disabled={saving}
                    sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    InputProps={{
                      endAdornment: (
                        <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                          L.L
                        </Typography>
                      ),
                    }}
                  />

                  {/* delete button — disabled when only one row remains */}
                  <Tooltip title={rows.length <= 1 ? "" : "Remove"}>
                    <span>
                      <IconButton
                        disabled={rows.length <= 1 || saving}
                        onClick={() => removeRow(idx)}
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: 2,
                          border: "1px solid rgba(0,0,0,.08)",
                        }}
                      >
                        <DeleteRoundedIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                {/* //to display error when overlap from to date  */}
                {rowErrors[idx] && (
                    <Box sx={{ px: 2, pb: 1.5, mt: 1 }}>
                    <Typography
                        variant="caption"
                        sx={{
                        color: "error.main",
                        fontWeight: 600,
                        display: "block",
                        }}
                    >
                        ⚠ {rowErrors[idx]}
                    </Typography>
                    </Box>
                )}

              </Paper>
            ))}

            {/* add date range button — same style as EditTimeDialog's add pair button */}
            <Button
              startIcon={<AddCircleRoundedIcon />}
              onClick={addRow}
              disabled={saving}
              sx={{
                alignSelf: "flex-start",
                fontWeight: 800,
                textTransform: "none",
                color: "#00796b",
              }}
            >
              Add date range
            </Button>

          </Stack>
        </LocalizationProvider>
      </DialogContent>

      {/* actions — same gradient save button as EditTimeDialog */}
      <DialogActions sx={{ p: 1.5 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{ fontWeight: 800, textTransform: "none" }}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} /> : <SaveRoundedIcon />}
          sx={{
            fontWeight: 900,
            textTransform: "none",
            borderRadius: 2,
            background: "linear-gradient(90deg,#00796b,#26a69a)",
            boxShadow: "0 12px 26px rgba(0,0,0,.16)",
          }}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}